import { FC, ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useAuctionPricing } from 'dibs-auctions/exports/hooks';
import classnames from 'classnames';

// components
import { Link } from 'dibs-elements/exports/Link';
import { FormattedMessage } from 'dibs-react-intl';
import { PriceDisplay, FormattedPrice } from 'dibs-buyer-price';
import { useAuctionSaleTreatmentVariant } from '../hooks/useAuctionSaleTreatmentVariant';
import { PdpSharedAuctionTooltipLazy } from '../PdpSharedAuctionToolTip/PdpSharedAuctionTooltipLazy';
import { OriginalListPrice } from 'dibs-auctions/exports/OriginalListPrice';
import { PdpSharedAuctionPercentageOff } from '../shared/PdpSharedAuctionPercentageOff/PdpSharedAuctionPercentageOff';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

// types
import { PdpSharedAuctionPreview_item$key as Item } from './__generated__/PdpSharedAuctionPreview_item.graphql';

// styles
import styles from './PdpSharedAuctionPreview.scss';
import dibsCss from 'dibs-css';

type Props = {
    item: Item;
    currency: string;
    promoCode: ReactNode;
};

const itemFragment = graphql`
    fragment PdpSharedAuctionPreview_item on Item {
        ...PriceDisplay_item
            @arguments(
                userId: $userId
                isTrade: $isTrade
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
            )
        ...useAuctionPricing_item @arguments(page: pdp)
        ...OriginalListPrice_item
        ...PdpSharedAuctionCountdown_item
        ...PdpSharedAuctionPercentageOff_item
    }
`;

const copy = {
    startingBid: (
        <FormattedMessage id="PdpSharedAuctionPreview.startingBid" defaultMessage="Starting Bid" />
    ),
};

export const PdpSharedAuctionPreview: FC<Props> = ({ item: itemRef, currency, promoCode }) => {
    const item = useFragment(itemFragment, itemRef);
    const { buyItNowAmount, hasReserve } = useAuctionPricing({
        item,
        currency,
    });
    const isAuctionSaleTreatmentVariant = useAuctionSaleTreatmentVariant();
    const { isMobile } = useServerVarsContext();

    return isAuctionSaleTreatmentVariant ? (
        <>
            <div className={classnames(dibsCss.flex, dibsCss.gapXXxsmall, dibsCss.itemsBaseline)}>
                <PriceDisplay
                    textSize="largeHeavy"
                    currency={currency}
                    item={item}
                    sku={null}
                    useSvgIcon
                />
                <span className={classnames(dibsCss.sassyFontBodyTypeHeavySizeSmall)}>
                    {copy.startingBid}
                </span>
            </div>

            <PdpSharedAuctionPercentageOff item={item} currency={currency} />

            {buyItNowAmount && (
                <div
                    className={classnames(
                        dibsCss.sassyFontBodySizeSmall,
                        isMobile ? dibsCss.mtXsmall : dibsCss.mtSmall
                    )}
                >
                    <FormattedMessage
                        id="PdpSharedAuctionPreview.currentBid"
                        defaultMessage="{amount} Buy It Now"
                        values={{
                            amount: <FormattedPrice value={buyItNowAmount} currency={currency} />,
                        }}
                    />
                </div>
            )}
            <div className={styles.promoCode}>{promoCode}</div>
            <div className={styles.prompt}>
                <FormattedMessage
                    id="PdpSharedAuctionPreview.prompt"
                    defaultMessage="You can't bid on this item yet. Favorite this item to get updates on this auction. {link}"
                    values={{
                        link: (
                            <Link
                                href="/info/auctions-faq/"
                                target="_blank"
                                className={styles.link}
                            >
                                <FormattedMessage
                                    id="PdpSharedAuctionPreview.learnMore"
                                    defaultMessage="Learn More about Auctions"
                                />
                            </Link>
                        ),
                    }}
                />
            </div>
        </>
    ) : (
        <div className={styles.container}>
            <OriginalListPrice
                item={item}
                currency={currency}
                className={styles.originalListPrice}
            />
            {!hasReserve && (
                <span className={styles.noReserve}>
                    <FormattedMessage
                        id="PdpSharedAuctionPreview.noReserve"
                        defaultMessage="No Reserve"
                    />
                    <PdpSharedAuctionTooltipLazy type="reserve" />
                </span>
            )}
            <span className={styles.bid}>{copy.startingBid}</span>
            <PriceDisplay currency={currency} item={item} sku={null} useSvgIcon />
            {buyItNowAmount && (
                <span>
                    <FormattedMessage
                        id="PdpSharedAuctionPreview.currentBid"
                        defaultMessage="{amount} Buy It Now"
                        values={{
                            amount: <FormattedPrice value={buyItNowAmount} currency={currency} />,
                        }}
                    />
                </span>
            )}
            <div className={styles.promoCode}>{promoCode}</div>
            <div className={styles.prompt}>
                <FormattedMessage
                    id="PdpSharedAuctionPreview.prompt"
                    defaultMessage="You can't bid on this item yet. Favorite this item to get updates on this auction. {link}"
                    values={{
                        link: (
                            <Link
                                href="/info/auctions-faq/"
                                target="_blank"
                                className={styles.link}
                            >
                                <FormattedMessage
                                    id="PdpSharedAuctionPreview.learnMore"
                                    defaultMessage="Learn More about Auctions"
                                />
                            </Link>
                        ),
                    }}
                />
            </div>
        </div>
    );
};
